<template>
  <div>
    <!-- Hero -->
    <section class="hero">
      <div class="premium-container">
        <div class="hero-layout">
          <div class="hero-content fade-in">
            <span class="premium-label">Websites / Automation / AI</span>
            <h1 class="premium-heading-lg">
              We build the digital<br>systems that run<br>your business.
            </h1>
            <p class="premium-subheading hero-sub">
              Custom websites, intelligent automation, and AI-powered tools
              for UK businesses. Delivered fast, built to last.
            </p>
            <div class="hero-actions">
              <router-link to="/contact" class="premium-btn premium-btn-dark">
                Start a project
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </router-link>
              <router-link to="/services" class="premium-btn premium-btn-outline">
                View services
              </router-link>
            </div>
          </div>
          <div class="hero-visual fade-in fade-in-delay-2">
            <div class="visual-grid">
              <div class="visual-card">
                <div class="vc-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.5"/></svg>
                </div>
                <span class="vc-title">Custom Websites</span>
                <span class="vc-stat">Responsive & fast</span>
              </div>
              <div class="visual-card">
                <div class="vc-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
                </div>
                <span class="vc-title">Automation</span>
                <span class="vc-stat">Workflows & CRM</span>
              </div>
              <div class="visual-card">
                <div class="vc-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/><path d="M12 2V4M12 20V22M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07M2 12H4M20 12H22M4.93 19.07L6.34 17.66M17.66 6.34L19.07 4.93" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </div>
                <span class="vc-title">AI Tools</span>
                <span class="vc-stat">Chatbots & analytics</span>
              </div>
              <div class="visual-card">
                <div class="vc-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 3V21H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 16L11 11L15 14L21 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
                <span class="vc-title">Analytics</span>
                <span class="vc-stat">Data-driven growth</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services -->
    <section class="premium-section-alt">
      <div class="premium-container">
        <div class="section-header text-center fade-in">
          <span class="premium-label">What we do</span>
          <h2 class="premium-heading" style="max-width: 680px; margin-left: auto; margin-right: auto;">
            Everything you need to succeed online.
          </h2>
        </div>

        <div class="services-grid">
          <router-link
            v-for="service in services"
            :key="service.title"
            :to="service.link"
            class="premium-card service-card fade-in"
          >
            <div class="service-icon-wrap" v-html="service.icon"></div>
            <h3 class="service-title">{{ service.title }}</h3>
            <p class="service-desc premium-body">{{ service.description }}</p>
            <span class="service-arrow">
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- AI Spotlight -->
    <section class="premium-section">
      <div class="premium-container">
        <div class="section-header text-center fade-in">
          <span class="premium-label">AI-Powered</span>
          <h2 class="premium-heading" style="max-width: 560px; margin-left: auto; margin-right: auto;">
            Let AI handle the busywork.
          </h2>
          <p class="premium-subheading mx-auto" style="margin-bottom: 40px;">
            We integrate AI directly into your business tools — so you spend
            less time on repetitive tasks and more time growing.
          </p>
        </div>

        <div class="ai-grid">
          <div class="ai-feature fade-in" v-for="(item, index) in aiFeatures" :key="item.title" :class="'fade-in-delay-' + (index + 1)">
            <div class="ai-feature-icon" v-html="item.icon"></div>
            <h4 class="ai-feature-title">{{ item.title }}</h4>
            <p class="ai-feature-desc premium-body">{{ item.description }}</p>
          </div>
        </div>

        <div class="text-center" style="margin-top: 48px;">
          <router-link to="/services/ai-automation" class="premium-btn premium-btn-dark fade-in">
            Explore AI automation
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Case Study -->
    <section class="premium-section-alt">
      <div class="premium-container">
        <div class="section-header text-center fade-in">
          <span class="premium-label">Our work</span>
          <h2 class="premium-heading" style="max-width: 560px; margin-left: auto; margin-right: auto;">
            Real projects, real results.
          </h2>
        </div>

        <a
          href="https://www.openbandanalytics.com"
          target="_blank"
          rel="noopener noreferrer"
          class="case-card fade-in"
        >
          <div class="case-card-content">
            <h3 class="case-card-title">Openband Analytics</h3>
            <p class="case-card-desc premium-body">
              A real-time geomagnetic risk intelligence platform with live data visualisation,
              risk scoring, and automated alerts — designed and built from scratch.
            </p>
            <div class="case-tags">
              <span class="case-tag">Dashboard</span>
              <span class="case-tag">Real-time Data</span>
              <span class="case-tag">Analytics</span>
              <span class="case-tag">Custom Platform</span>
            </div>
          </div>
          <div class="case-card-visual">
            <div class="browser-frame">
              <div class="browser-dots">
                <span></span><span></span><span></span>
              </div>
              <div class="browser-url">openbandanalytics.com</div>
              <div class="browser-body">
                <div class="mock-header"></div>
                <div class="mock-grid">
                  <div class="mock-card"></div>
                  <div class="mock-card"></div>
                  <div class="mock-card"></div>
                </div>
                <div class="mock-chart"></div>
              </div>
            </div>
          </div>
          <span class="case-card-link">
            Visit website
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>
        </a>
      </div>
    </section>

    <!-- Why Digitly -->
    <section class="premium-section">
      <div class="premium-container">
        <div class="section-header text-center fade-in">
          <span class="premium-label">Why Digitly</span>
          <h2 class="premium-heading" style="max-width: 560px; margin-left: auto; margin-right: auto;">
            Fast delivery. Premium quality.
          </h2>
          <p class="premium-subheading mx-auto">
            We move fast without cutting corners. Most projects go
            from kickoff to live in 2–3 weeks.
          </p>
        </div>

        <div class="features-grid">
          <div
            v-for="(feature, index) in features"
            :key="feature.title"
            class="feature-item fade-in"
            :class="'fade-in-delay-' + (index + 1)"
          >
            <h4 class="feature-title">{{ feature.title }}</h4>
            <p class="feature-desc premium-body">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-section">
      <div class="premium-container">
        <div class="cta-block text-center fade-in">
          <h2 class="premium-heading" style="max-width: 600px; margin-left: auto; margin-right: auto;">
            Ready to build something great?
          </h2>
          <p class="premium-body mx-auto" style="margin-bottom: 40px; max-width: 500px;">
            Tell us about your project. We'll get back to you within 24 hours
            with a free consultation and clear quote.
          </p>
          <router-link to="/contact" class="premium-btn premium-btn-dark">
            Get in touch
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useSeo, useBreadcrumbSchema } from '@/composables/useSeo'

export default {
  name: 'Home',
  setup() {
    useSeo({
      title: 'Websites, Automation & AI for UK Businesses | Digitly',
      description: 'Custom websites, business automation, and AI-powered tools for UK businesses. Fast delivery in 2-3 weeks. From £499. Free consultation.',
      keywords: 'web development UK, business automation, AI automation, custom websites, construction website, beauty salon website, plumbing website, dental website, small business websites, fast web design UK',
      path: '/'
    })

    useBreadcrumbSchema([
      { name: 'Home', path: '/' }
    ])
  },
  data() {
    return {
      services: [
        {
          icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M8 22H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M12 18V22" stroke="currentColor" stroke-width="1.5"/></svg>',
          title: 'Web Development',
          description: 'Custom-built, responsive websites that look premium and load fast. No templates, no compromises.',
          link: '/services/web-development'
        },
        {
          icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/><path d="M3 12H9M15 12H21M12 3V9M12 15V21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
          title: 'AI & Automation',
          description: 'Chatbots, automated workflows, smart booking systems, and AI tools that save you hours every week.',
          link: '/services/ai-automation'
        },
        {
          icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M3 6H21" stroke="currentColor" stroke-width="1.5"/><path d="M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
          title: 'E-Commerce',
          description: 'Online stores that convert. Secure payments, inventory management, and a checkout your customers love.',
          link: '/services/ecommerce'
        },
        {
          icon: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="1.5"/><path d="M12 6V12L16 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
          title: 'Maintenance & Support',
          description: 'Ongoing care for your digital products. Security monitoring, updates, and performance optimisation.',
          link: '/services/maintenance'
        }
      ],
      aiFeatures: [
        {
          icon: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>',
          title: 'AI Chatbots',
          description: 'Intelligent chat that answers customer questions, books appointments, and captures leads 24/7.'
        },
        {
          icon: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8" stroke="currentColor" stroke-width="1.5"/><path d="M15 2H9C8.44772 2 8 2.44772 8 3V5C8 5.55228 8.44772 6 9 6H15C15.5523 6 16 5.55228 16 5V3C16 2.44772 15.5523 2 15 2Z" stroke="currentColor" stroke-width="1.5"/></svg>',
          title: 'Smart Workflows',
          description: 'Automated follow-ups, invoicing, scheduling, and data entry — triggered by your business rules.'
        },
        {
          icon: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 3V21H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 16L11 11L15 14L21 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          title: 'Predictive Analytics',
          description: 'AI-driven dashboards that surface insights, spot trends, and help you make better business decisions.'
        },
        {
          icon: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 4H10V10H4V4Z" stroke="currentColor" stroke-width="1.5"/><path d="M14 4H20V10H14V4Z" stroke="currentColor" stroke-width="1.5"/><path d="M4 14H10V20H4V14Z" stroke="currentColor" stroke-width="1.5"/><path d="M17 14V20M14 17H20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
          title: 'Custom Integrations',
          description: 'Connect your tools — CRM, email, accounting, booking — into one seamless automated system.'
        }
      ],
      features: [
        {
          title: 'Built fast, built right',
          description: 'Most projects launch in 2–3 weeks. We move quickly without sacrificing quality or cutting corners.'
        },
        {
          title: 'Bespoke, not template',
          description: 'Every project is custom-built from scratch to match your brand, your goals, and your customers.'
        },
        {
          title: 'AI-ready from day one',
          description: 'We build with automation and AI in mind, so your tools can grow smarter as your business scales.'
        },
        {
          title: 'Ongoing partnership',
          description: 'We don\'t disappear after launch. Dedicated support and a long-term relationship you can rely on.'
        }
      ]
    }
  }
}
</script>

<style scoped>
.hero {
  padding: 140px 0 80px;
  background: var(--color-bg);
}

.hero-layout {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 64px;
  align-items: center;
}

.hero-content {
  max-width: 620px;
}

.hero-sub {
  margin-bottom: 32px;
}

.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

/* Hero visual grid */
.visual-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.visual-card {
  background: var(--color-bg-alt);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.visual-card:hover {
  border-color: #C8C8CC;
  transform: translateY(-2px);
}

.vc-icon {
  color: var(--color-text);
}

.vc-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.vc-stat {
  font-size: 0.8125rem;
  color: var(--color-text-tertiary);
}

.section-header {
  margin-bottom: 64px;
}

/* Services grid - clickable cards */
.services-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.service-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  position: relative;
}

.service-card:hover {
  border-color: #C8C8CC;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  transform: translateY(-4px);
}

.service-icon-wrap {
  color: var(--color-text);
  margin-bottom: 20px;
  line-height: 0;
}

.service-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 12px;
  letter-spacing: -0.02em;
}

.service-desc {
  margin-bottom: 20px;
  flex: 1;
}

.service-arrow {
  color: var(--color-text-tertiary);
  transition: transform 0.2s ease, color 0.2s ease;
}

.service-card:hover .service-arrow {
  color: var(--color-text);
  transform: translateX(4px);
}

/* AI Spotlight - centered grid */
.ai-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.ai-feature {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.ai-feature-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-alt);
  border: 1px solid var(--color-border);
  border-radius: 14px;
  color: var(--color-text);
}

.ai-feature-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.ai-feature-desc {
  font-size: 0.9375rem;
}

/* Case Study - hoverable card */
.case-card {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 48px;
  align-items: center;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 48px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
}

.case-card:hover {
  border-color: #C8C8CC;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
}

.case-card-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 12px;
  letter-spacing: -0.02em;
}

.case-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
}

.case-tag {
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  background: var(--color-bg-alt);
  padding: 6px 14px;
  border-radius: 980px;
  border: 1px solid var(--color-border);
}

.case-card-link {
  position: absolute;
  bottom: 48px;
  left: 48px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-link);
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.case-card:hover .case-card-link {
  opacity: 1;
  transform: translateY(0);
}

/* Browser mockup */
.browser-frame {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.browser-dots {
  display: flex;
  gap: 6px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border);
}

.browser-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-border);
}

.browser-url {
  padding: 8px 16px;
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  background: var(--color-bg-alt);
  border-bottom: 1px solid var(--color-border);
}

.browser-body {
  padding: 20px;
  min-height: 200px;
}

.mock-header {
  height: 12px;
  width: 40%;
  background: var(--color-bg-alt);
  border-radius: 6px;
  margin-bottom: 16px;
}

.mock-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.mock-card {
  height: 48px;
  background: var(--color-bg-alt);
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.mock-chart {
  height: 72px;
  background: linear-gradient(135deg, var(--color-bg-alt) 0%, #E8E8ED 100%);
  border-radius: 8px;
}

/* Why Digitly - centered grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 48px 64px;
}

.feature-item {
  text-align: center;
}

.feature-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}

/* CTA */
.cta-section {
  padding: 120px 0;
  background: var(--color-bg-alt);
  border-top: 1px solid var(--color-border);
}

.cta-block {
  padding: 0;
}

@media (max-width: 960px) {
  .hero {
    /* Tighter top: main already has 64px for fixed header; avoid double “air” */
    padding: 56px 0 56px;
  }

  .hero-layout {
    grid-template-columns: 1fr;
    gap: 48px;
  }

  .hero-visual {
    display: none;
  }

  .services-grid {
    grid-template-columns: 1fr;
  }

  .ai-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .case-card {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 32px;
  }

  .case-card-link {
    position: static;
    opacity: 1;
    transform: none;
    margin-top: 16px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .feature-item {
    text-align: left;
  }

  .cta-section {
    padding: 80px 0;
  }
}

@media (max-width: 600px) {
  .hero {
    padding: 36px 0 48px;
  }

  .hero-actions {
    flex-direction: column;
  }

  .hero-actions .premium-btn {
    text-align: center;
    justify-content: center;
  }

  .ai-grid {
    grid-template-columns: 1fr;
  }
}
</style>
